import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { sessionHasPermission } from 'shared/utils/sessionPermissions';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import {
  getCachedJobPosition,
  getRegistryTenantIdFromSession,
  profileJobPosition,
} from 'shared/utils/sessionProfileStore';
import { incidentApi, softwareApi } from '../../api/systemRegisterApi';
import { INCIDENT_SEVERITY, formatEnumLabel } from '../../constants/enums';
import DeploymentStageApprovalDialog from '../../shared/DeploymentStageApprovalDialog';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
  reportKeepTogetherSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const WIZARD_SNAPSHOT_KEY = 'sr-register-wizard-snapshot';
const FORM_PRINT_ROOT_ID = 'incident-escalation-form';
const PRINTING_BODY_CLASS = 'sr-printing-incident-form';

const FIRST_INCIDENT_APPROVAL_PERMISSION = 'PERFORM_FIRST_INCIDENT_APPROVAL';

function normalizePersonKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function matchesIncidentPerson(stored, name, email) {
  const raw = normalizePersonKey(stored);
  if (!raw) return false;
  const n = normalizePersonKey(name);
  const e = normalizePersonKey(email);
  if (e && (raw === e || raw.includes(e))) return true;
  if (n && (raw === n || raw.startsWith(`${n} (`))) return true;
  if (n && e) {
    const label = normalizePersonKey(`${name} (${email})`);
    if (raw === label) return true;
  }
  return false;
}

function isIncidentAdmin(user) {
  const payload = user?.data;
  if (!payload) return false;
  const names = [];
  if (Array.isArray(payload.roles) && payload.roles.length > 0) {
    names.push(...payload.roles);
  }
  if (payload.role) {
    names.push(payload.role);
  }
  return names.some((roleName) => {
    const normalized = String(roleName ?? '')
      .trim()
      .toUpperCase()
      .replace(/[\s_-]+/g, '');
    return normalized === 'ADMIN' || normalized === 'SYSTEMADMIN';
  });
}

function isIncidentReporter(incident, currentUser) {
  const name = currentUser?.name || '';
  const email = currentUser?.email || '';
  return (
    matchesIncidentPerson(incident?.reportedBy, name, email)
    || matchesIncidentPerson(incident?.incidentReportingStaff, name, email)
  );
}

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '';
}

function formatIncidentDateTime(value) {
  if (value == null || value === '') return '';
  const raw = String(value);
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return raw.includes('T') ? raw.replace('T', ' ').slice(0, 16) : raw;
}

function slotRole(slot) {
  return slot === 1 ? '1st Approval' : '2nd Approval';
}

function slotButtonLabel(slot) {
  return slot === 1 ? '1st Approval' : 'Reporter approval';
}

function findSlotApproval(approvals, slot) {
  const role = slotRole(slot);
  return (approvals ?? []).find((a) => a?.approverRole === role) || null;
}

function approvalDecisionLabel(approval) {
  if (!approval?.approvalStatus) return null;
  if (approval.approvalStatus === 'APPROVED') return 'APPROVED';
  if (approval.approvalStatus === 'REJECTED') return 'DECLINED';
  return formatEnumLabel(approval.approvalStatus);
}

function jobPositionForApproval(approval, users, currentUser) {
  const stored = approval?.jobPosition && String(approval.jobPosition).trim();
  if (stored) return stored;
  const email = normalizePersonKey(approval?.signatureConfirmation);
  const name = normalizePersonKey(approval?.approverName);
  const cached = getCachedJobPosition({
    email: approval?.signatureConfirmation,
    name: approval?.approverName,
  });
  if (cached) return cached;
  const match =
    (email && users.find((u) => normalizePersonKey(u.email) === email))
    || (name && users.find((u) => normalizePersonKey(u.name) === name))
    || null;
  if (match?.jobPosition) return String(match.jobPosition).trim();
  if (
    currentUser
    && (
      (email && email === normalizePersonKey(currentUser.email))
      || (name && name === normalizePersonKey(currentUser.name))
    )
    && currentUser.jobPosition
  ) {
    return String(currentUser.jobPosition).trim();
  }
  return '';
}

function patchWizardSnapshotIncident(updatedIncident) {
  try {
    const raw = sessionStorage.getItem(WIZARD_SNAPSHOT_KEY);
    if (!raw || !updatedIncident) return;
    const snap = JSON.parse(raw);
    const entries = snap?.wizardData?.incidentEscalation?.entries;
    if (!Array.isArray(entries)) return;
    const next = entries.map((e) => {
      const sameKey = updatedIncident.key && e.key === updatedIncident.key;
      const sameId = updatedIncident.id != null && e.id === updatedIncident.id;
      return sameKey || sameId ? { ...e, ...updatedIncident } : e;
    });
    snap.wizardData.incidentEscalation.entries = next;
    sessionStorage.setItem(WIZARD_SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {
    // ignore snapshot sync failures
  }
}

function normalizeIncidentRecord(matched) {
  if (!matched) return null;
  return {
    id: matched.id ?? null,
    incidentTicket: matched.incidentTicket ?? '',
    incidentDate: matched.incidentDate ?? '',
    reportedAt: matched.reportedAt ?? matched.incidentDate ?? '',
    solvedApprovedAt: matched.solvedApprovedAt ?? '',
    incidentDetails: matched.incidentDetails ?? '',
    actionTaken: matched.actionTaken ?? '',
    reportedBy: matched.reportedBy ?? '',
    incidentReportingStaff: matched.incidentReportingStaff ?? '',
    severityLevel: matched.severityLevel ?? '',
    resolutionStatus: matched.resolutionStatus ?? 'OPEN',
    incidentResponseOwner: matched.incidentResponseOwner ?? '',
    knownIncidents: Boolean(matched.knownIncidents ?? true),
    escalationPathDefined: Boolean(matched.escalationPathDefined),
    rootCause: matched.rootCause ?? '',
    softwareId: matched.softwareId ?? null,
    softwareName: matched.softwareName ?? '',
    assetRegisterIds: matched.assetRegisterIds ?? [],
    assets: matched.assets ?? [],
    approvals: (matched.approvals ?? []).map((a) => ({
      id: a.id ?? null,
      incidentId: a.incidentId ?? matched.id ?? null,
      approverName: a.approverName ?? '',
      approverRole: a.approverRole ?? '',
      approverCommittee: a.approverCommittee ?? '',
      approvalDate: a.approvalDate ?? '',
      approvalStatus: a.approvalStatus ?? '',
      approvalComments: a.approvalComments ?? '',
      approvalLevel: a.approvalLevel ?? '',
      signatureConfirmation: a.signatureConfirmation ?? '',
      jobPosition: a.jobPosition ?? a.job_position ?? '',
    })),
  };
}

function extractIncidentFromSoftwareDetail(detail, incidentId, incidentKey) {
  const list = detail?.incidents ?? [];
  const matched =
    list.find((i) => incidentId != null && i.id === incidentId)
    || list.find((i) => incidentKey && String(i.key) === String(incidentKey));
  if (!matched) return null;
  return normalizeIncidentRecord(matched);
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

/**
 * Paper-style Incident & Escalation form with 1st/2nd approval actions.
 */
export default function IncidentViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const querySoftwareId = searchParams.get('softwareId');
  const queryIncidentId = searchParams.get('id');

  const [softwareName, setSoftwareName] = useState(location.state?.softwareName || '');
  const [softwareId, setSoftwareId] = useState(location.state?.softwareId ?? null);

  const [incident, setIncident] = useState(() => location.state?.incident || null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSlot, setDialogSlot] = useState(1);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);

  useEffect(() => {
    if (location.state?.incident) {
      setIncident(location.state.incident);
      setSoftwareName(location.state.softwareName || '');
      setSoftwareId(location.state.softwareId ?? null);
      setLoadError('');
      return;
    }

    if (!queryIncidentId) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        if (querySoftwareId) {
          const response = await softwareApi.get(querySoftwareId);
          const detail = unwrapData(response);
          if (cancelled) return;
          const fromApi = extractIncidentFromSoftwareDetail(
            detail,
            Number(queryIncidentId),
            null
          );
          if (!fromApi) {
            setIncident(null);
            setLoadError('Incident not found for this software.');
            return;
          }
          setIncident(fromApi);
          setSoftwareName(detail?.softwareName || '');
          setSoftwareId(detail?.id ?? Number(querySoftwareId));
          return;
        }

        const response = await incidentApi.get(queryIncidentId);
        const detail = unwrapData(response);
        if (cancelled) return;
        const fromApi = normalizeIncidentRecord(detail);
        if (!fromApi) {
          setIncident(null);
          setLoadError('Incident not found.');
          return;
        }
        setIncident(fromApi);
        setSoftwareName(detail?.softwareName || '');
        setSoftwareId(detail?.softwareId ?? null);
      } catch (error) {
        if (!cancelled) {
          setIncident(null);
          setLoadError(apiErrorMessage(error, 'Failed to load incident details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [location.state, querySoftwareId, queryIncidentId]);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      const registryTenantId = getRegistryTenantIdFromSession();
      if (!registryTenantId) return;
      try {
        const rows = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
        if (!cancelled) setTenantUsers(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setTenantUsers([]);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  const approvals = incident?.approvals ?? [];
  const firstApproval = findSlotApproval(approvals, 1);
  const secondApproval = findSlotApproval(approvals, 2);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const isAdmin = isIncidentAdmin(user);
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [
      isDark,
      tenantBranding?.backgroundColor,
      tenantBranding?.primaryColor,
      tenantBranding?.secondaryColor,
    ]
  );
  const tenantPhoto = tenantBranding?.tenantPhoto;
  const tenantLogoSrc = useProfilePhotoSrc(tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  const currentUser = useMemo(
    () => ({
      name: profile?.name?.trim() || profile?.fullName?.trim() || '',
      email: profile?.email?.trim() || '',
      jobPosition:
        profileJobPosition(profile)
        || getCachedJobPosition({
          id: profile?.id,
          email: profile?.email,
          name: profile?.name || profile?.fullName,
        }),
    }),
    [profile]
  );

  const canFirstApproval =
    isAdmin || sessionHasPermission(profile, FIRST_INCIDENT_APPROVAL_PERMISSION);
  const canSecondApproval = isAdmin || isIncidentReporter(incident, currentUser);
  const firstApproved = firstApproval?.approvalStatus === 'APPROVED';

  const applyLocalApproval = useCallback(
    ({ slot, decision, comment }) => {
      const role = slotRole(slot);
      const declined = decision === 'DECLINED';
      const today = new Date().toISOString().slice(0, 10);
      const displayName = currentUser.name || currentUser.email || 'Approver';
      const email = currentUser.email || '';
      const nextApproval = {
        id: findSlotApproval(incident?.approvals, slot)?.id ?? null,
        incidentId: incident?.id ?? null,
        approverName: displayName,
        approverRole: role,
        approverCommittee: '',
        approvalDate: today,
        approvalStatus: declined ? 'REJECTED' : 'APPROVED',
        approvalComments:
          `${declined ? 'Declined' : 'Approved'} incident`
          + (email ? ` | Approver email: ${email}` : '')
          + (comment ? ` | Comment: ${comment}` : ''),
        approvalLevel: slot === 1 ? 'TECHNICAL' : 'BUSINESS',
        signatureConfirmation: email,
        jobPosition: currentUser.jobPosition || '',
      };

      const withoutSlot = (incident?.approvals ?? []).filter((a) => a?.approverRole !== role);
      const nextApprovals = [...withoutSlot, nextApproval];
      const anyDeclined = nextApprovals.some((a) => a.approvalStatus === 'REJECTED');
      const staffApproved = nextApprovals.some(
        (a) => a.approverRole === '1st Approval' && a.approvalStatus === 'APPROVED'
      );
      const reporterApproved = nextApprovals.some(
        (a) => a.approverRole === '2nd Approval' && a.approvalStatus === 'APPROVED'
      );
      const nextStatus = anyDeclined
        ? 'IN_PROGRESS'
        : reporterApproved
          ? 'CLOSED'
          : staffApproved
            ? 'RESOLVED'
            : incident?.resolutionStatus;
      const nowIso = new Date().toISOString();

      const updated = {
        ...incident,
        resolutionStatus: nextStatus,
        solvedApprovedAt: anyDeclined
          ? ''
          : (reporterApproved ? (incident?.solvedApprovedAt || nowIso) : incident?.solvedApprovedAt),
        approvals: nextApprovals,
      };
      setIncident(updated);
      patchWizardSnapshotIncident(updated);
      return updated;
    },
    [currentUser.email, currentUser.jobPosition, currentUser.name, incident]
  );

  const openApprovalDialog = (slot) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) {
      toast.error('You do not have permission for this approval.');
      return;
    }
    if (slot === 2 && !firstApproved) {
      toast.error('1st Approval is required before the reporter can confirm the incident is solved.');
      return;
    }
    if (!currentUser.name && !currentUser.email) {
      toast.error('Logged-in user name or email is required to approve or decline.');
      return;
    }
    setDialogSlot(slot);
    setDialogOpen(true);
  };

  const handleApprovalSubmit = async ({ decision, comment }) => {
    setSaving(true);
    try {
      if (!incident?.id) {
        applyLocalApproval({ slot: dialogSlot, decision, comment });
      } else if (softwareId) {
        const response = await softwareApi.recordIncidentApproval(softwareId, incident.id, {
          slot: dialogSlot,
          name: currentUser.name || currentUser.email,
          email: currentUser.email || '',
          jobPosition: currentUser.jobPosition || '',
          approvalDate: new Date().toISOString().slice(0, 10),
          decision,
          comment,
        });
        const detail = unwrapData(response);
        const fromApi = extractIncidentFromSoftwareDetail(detail, incident.id, incident.key);
        if (fromApi) {
          const updated = { ...fromApi, key: incident.key };
          setIncident(updated);
          patchWizardSnapshotIncident(updated);
        } else {
          applyLocalApproval({ slot: dialogSlot, decision, comment });
        }
      } else {
        const response = await incidentApi.recordApproval(incident.id, {
          slot: dialogSlot,
          name: currentUser.name || currentUser.email,
          email: currentUser.email || '',
          jobPosition: currentUser.jobPosition || '',
          approvalDate: new Date().toISOString().slice(0, 10),
          decision,
          comment,
        });
        const detail = unwrapData(response);
        const fromApi = normalizeIncidentRecord(detail);
        if (fromApi) {
          const updated = { ...fromApi, key: incident.key };
          setIncident(updated);
          patchWizardSnapshotIncident(updated);
        } else {
          applyLocalApproval({ slot: dialogSlot, decision, comment });
        }
      }
      toast.success(
        decision === 'DECLINED'
          ? `${slotRole(dialogSlot)} declined.`
          : `${slotRole(dialogSlot)} approved.`
      );
      setDialogOpen(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save incident approval.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl || !incident || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const labelBase = (incident.incidentDetails || softwareName || 'incident')
        .replace(/[^\w\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 48) || 'incident';
      await downloadFormPdf({
        element: formEl,
        fileName: `${labelBase}-INCIDENT-ESCALATION-FORM.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, incident, softwareName]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-incident-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 12mm !important;
          box-sizing: border-box !important;
          box-shadow: none !important;
          overflow: visible !important;
          border: none !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
      document.body.classList.remove(PRINTING_BODY_CLASS);
    };
  }, []);

  const renderApprovalButton = (slot, approval) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) return null;

    const decision = approvalDecisionLabel(approval);
    const label = slotButtonLabel(slot);
    const blockedSecond = slot === 2 && !firstApproved && !decision;
    return (
      <Button
        key={slot}
        variant={decision === 'APPROVED' ? 'contained' : 'outlined'}
        color={decision === 'DECLINED' ? 'error' : decision === 'APPROVED' ? 'success' : 'secondary'}
        onClick={() => openApprovalDialog(slot)}
        disabled={saving || blockedSecond}
        sx={{ textTransform: 'none' }}
      >
        {decision ? `${label} (${decision})` : label}
      </Button>
    );
  };

  const renderApprovalCell = () => {
    if (!firstApproval && !secondApproval) {
      return (
        <Typography sx={{ fontSize: '0.85rem', color: 'inherit', opacity: 0.7, pl: 2 }}>
          —
        </Typography>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={1.5} sx={{ pl: 2, pr: 1.5, py: 1 }}>
        {[firstApproval, secondApproval].filter(Boolean).map((a) => {
          const roleLabel = a.approverRole === '2nd Approval'
            ? 'Reporter approval'
            : (a.approverRole || 'Approval');
          const signatureEmail = a.signatureConfirmation || '';
          const jobPosition = jobPositionForApproval(a, tenantUsers, currentUser);
          return (
            <Box
              key={a.approverRole || a.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                columnGap: 4,
                rowGap: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'inherit' }}>
                  {roleLabel}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: 'inherit' }}>
                  {a.approverName || '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'inherit' }}>
                  {approvalDecisionLabel(a) || '—'}
                  {a.approvalDate ? ` — ${a.approvalDate}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.75 }}>
                  Signature
                </Typography>
                <Typography
                  sx={{
                    fontFamily:
                      '"Segoe Script", "Lucida Handwriting", "Bradley Hand", "Comic Sans MS", cursive',
                    fontSize: '1.05rem',
                    lineHeight: 1.3,
                    color: 'inherit',
                  }}
                >
                  {jobPosition || '—'}
                </Typography>
                {signatureEmail ? (
                  <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.85 }}>
                    {signatureEmail}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!incident) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Incident & Escalation Form" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" mb={2}>
            {loadError
              || 'Open an incident from the Incident & Escalation step using the view action.'}
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header title="Incident & Escalation Form" />
      </Box>

      <Box
        className="no-print"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {renderApprovalButton(1, firstApproval)}
          {renderApprovalButton(2, secondApproval)}
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Button
            type="button"
            startIcon={<PrintOutlinedIcon />}
            onClick={handlePrint}
            variant="outlined"
          >
            Print
          </Button>
          <Button
            type="button"
            startIcon={
              downloadingPdf ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PictureAsPdfOutlinedIcon />
              )
            }
            onClick={handleDownloadPdf}
            variant="contained"
            color="secondary"
            disabled={downloadingPdf}
          >
            {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
          </Button>
        </Box>
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}` : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>

          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>

          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              Incident and Escalation Form
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR IE1
            </Typography>
          </Box>
        </Box>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell
                label={softwareName ? 'Software / System Name:' : 'IT Asset / Software:'}
                border={form.border}
                muted={form.muted}
                sx={{ width: '34%' }}
              >
                {dash(softwareName)
                  || (incident.assets || [])
                    .map((a) => a.assetName || a.assetId)
                    .filter(Boolean)
                    .join(', ')
                  || '—'}
              </FieldCell>
              <FieldCell label="Reported By:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(incident.reportedBy) || dash(incident.incidentReportingStaff) || '—'}
              </FieldCell>
              <FieldCell label="Status:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatEnumLabel(incident.resolutionStatus) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Severity / Risk Rating (Please Select):" border={form.border} muted={form.muted} colSpan={3}>
                <Box display="flex" flexWrap="wrap" gap={{ xs: 0.5, sm: 2 }}>
                  {INCIDENT_SEVERITY.map((level) => (
                    <FormControlLabel
                      key={level}
                      control={
                        <Checkbox
                          size="small"
                          checked={incident.severityLevel === level}
                          readOnly
                          disableRipple
                          tabIndex={-1}
                          sx={{
                            py: 0.25,
                            color: form.checkbox || 'inherit',
                            '&.Mui-checked': { color: form.checkbox || 'primary.main' },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '0.85rem', color: 'inherit' }}>
                          {formatEnumLabel(level)}
                        </Typography>
                      }
                      sx={{ mr: 1, ml: 0, color: 'inherit' }}
                    />
                  ))}
                </Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Incident Ticket:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(incident.incidentTicket) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Reported at:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {formatIncidentDateTime(incident.reportedAt || incident.incidentDate) || '—'}
              </FieldCell>
              <FieldCell label="Approved as solved:" border={form.border} muted={form.muted} sx={{ width: '50%' }} colSpan={2}>
                {formatIncidentDateTime(incident.solvedApprovedAt) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Incident Response Owner:" border={form.border} muted={form.muted} sx={{ width: '50%' }}>
                {dash(incident.incidentResponseOwner) || '—'}
              </FieldCell>
              <FieldCell label="Reporting Staff:" border={form.border} muted={form.muted} sx={{ width: '50%' }} colSpan={2}>
                {dash(incident.incidentReportingStaff) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Incident Description:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(incident.incidentDetails) || '—'}</Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Root Cause:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(incident.rootCause) || '—'}</Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Corrective Action Taken:" border={form.border} muted={form.muted} colSpan={3}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{dash(incident.actionTaken) || '—'}</Box>
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box data-pdf-keep="true" sx={reportKeepTogetherSx}>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  border: form.border,
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  bgcolor: form.headBg,
                  color: 'inherit',
                  py: 1,
                  '@media print': {
                    bgcolor: '#f5f5f5 !important',
                    color: '#111 !important',
                    border: '1px solid #1a1a1a !important',
                  },
                }}
              >
                Approval
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  border: form.border,
                  verticalAlign: 'top',
                  minHeight: 88,
                  py: 1.25,
                  color: 'inherit',
                }}
              >
                {renderApprovalCell()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box
          sx={{
            borderTop: form.border,
            py: 1.25,
            px: 1.5,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: form.muted,
            '@media print': { color: '#333 !important', borderTop: '1px solid #1a1a1a !important' },
          }}
        >
          Please retain this form for audit and compliance.
        </Box>
        </Box>
      </Paper>

      <DeploymentStageApprovalDialog
        open={dialogOpen}
        onClose={() => (saving ? null : setDialogOpen(false))}
        onSubmit={handleApprovalSubmit}
        stageStatus="INCIDENT"
        slot={dialogSlot}
        currentUser={currentUser}
        saving={saving}
        existingComment=""
        title={`${slotButtonLabel(dialogSlot)} — Incident`}
        description={
          dialogSlot === 2
            ? 'Confirm that this incident was solved successfully, or decline it and leave a comment.'
            : 'Approve this incident as solved by staff, or decline it and leave a comment.'
        }
      />
    </Box>
  );
}
